import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useOrg } from '../../context/OrgContext';
import { usePermissions } from '../../context/PermissionsContext';
import { getCustomerById } from '../../services/customer.service';
import { getCustomerCrops } from '../../services/customerCrop.service';
import { getAllCropTypes } from '../../services/cropType.service';
import { getAllRegions } from '../../services/region.service';
import { getAllSeasonTimes } from '../../services/seasonTime.service';
import { Timestamp } from 'firebase/firestore';
import { getAllLogs, addLog, updateLog, deleteLog, type CustomerLog } from '../../services/customerLog.service';
import { Navbar } from '../../components/Navbar';
import { BackButton, CalendarIcon, Icon, ReminderModal, Spinner, type ReminderCrop, type ReminderCustomer } from '../../components/core';
import { nextSeasonDate } from '../../utils/nextSeasonDate';
import { AddNoteForm } from './AddNoteForm';
import { LogList } from './LogList';

export default function CustomerLogsPage() {
  const { user, signOut } = useAuth();
  const { org, orgId, orgSlug } = useOrg();
  const { hasPermission } = usePermissions();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [customerName, setCustomerName] = useState('');
  const [customerInfo, setCustomerInfo] = useState<ReminderCustomer | undefined>(undefined);
  const [allLogs, setAllLogs] = useState<CustomerLog[]>([]);
  const [reminderCrops, setReminderCrops] = useState<ReminderCrop[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderDescription, setReminderDescription] = useState('');

  useEffect(() => {
    if (!id || !orgId) return;

    const load = async () => {
      setLoading(true);
      try {
        const customer = await getCustomerById(orgId, id);
        if (!customer || customer.isDeleted) {
          setNotFound(true);
          return;
        }
        setCustomerName(`${customer.firstName} ${customer.lastName}`);
        setCustomerInfo({ fullName: `${customer.firstName} ${customer.lastName}`, phone: customer.phone, address: customer.address });

        const [logsResult, cropsResult, cropTypesResult, regionsResult, seasonTimesResult] = await Promise.allSettled([
          getAllLogs(orgId, id),
          getCustomerCrops(orgId, id),
          getAllCropTypes(orgId),
          getAllRegions(orgId),
          getAllSeasonTimes(orgId),
        ]);

        if (logsResult.status === 'fulfilled') setAllLogs(logsResult.value);

        if (
          cropsResult.status === 'fulfilled' &&
          cropTypesResult.status === 'fulfilled' &&
          regionsResult.status === 'fulfilled'
        ) {
          const cropTypeMap = new Map(cropTypesResult.value.map(ct => [ct.id, ct.name]));
          const regionMap = new Map(regionsResult.value.map(r => [r.id, r.name]));
          const seasonMap = seasonTimesResult.status === 'fulfilled'
            ? new Map(seasonTimesResult.value.map(s => [`${s.cropTypeId}_${s.regionId}`, s]))
            : new Map();
          setReminderCrops(
            cropsResult.value
              .filter(c => !c.isDeleted)
              .map(c => {
                const season = seasonMap.get(`${c.cropTypeId}_${c.regionId}`);
                return {
                  id: `${c.cropTypeId}_${c.regionId}`,
                  cropTypeName: cropTypeMap.get(c.cropTypeId) ?? c.cropTypeId,
                  regionName: regionMap.get(c.regionId) ?? c.regionId,
                  seasonStartDate: season
                    ? nextSeasonDate(season.startMonth, season.startDay)
                    : undefined,
                };
              }),
          );
        }
      } catch (err) {
        console.error('[CustomerLogsPage] load error:', err);
        setPageError(t('customers.logsLoadError'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, orgId, t]);

  async function handleSaveNote(text: string) {
    if (!id || !orgId || !user) return;
    const newLog = await addLog(orgId, id, {
      message: text,
      createdBy: user.uid,
      userDisplayName: user.displayName ?? user.email ?? user.uid,
    });
    setAllLogs(prev => [newLog, ...prev]);
  }

  async function handleEditLog(logId: string, newMessage: string) {
    if (!id || !orgId) return;
    await updateLog(orgId, id, logId, newMessage);
    setAllLogs(prev =>
      prev.map(log =>
        log.id === logId
          ? { ...log, message: newMessage, updatedAt: Timestamp.fromDate(new Date()) }
          : log,
      ),
    );
  }

  async function handleDeleteLog(logId: string) {
    if (!id || !orgId) return;
    await deleteLog(orgId, id, logId);
    setAllLogs(prev => prev.filter(log => log.id !== logId));
  }

  function handleRemindFromLog(logMessage: string) {
    setReminderDescription(logMessage);
    setReminderOpen(true);
  }

  function handleOpenReminderFromHeader() {
    setReminderDescription('');
    setReminderOpen(true);
  }

  return (
    <div className="min-h-screen bg-green-50">
      <Navbar displayName={user?.displayName ?? null} onSignOut={signOut} onLogoClick={() => navigate('/')} onOrgNameClick={() => navigate(`/org/${orgSlug}`)} onAvatarClick={() => navigate('/profile')} orgName={org.name} />

      <main className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        <BackButton />

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : pageError ? (
          <div className="rounded bg-red-50 px-4 py-3 font-body text-sm text-red-600">
            {pageError}
          </div>
        ) : notFound ? (
          <div className="py-16 text-center">
            <p className="font-display text-base font-semibold text-gray-700">{t('customers.notFound')}</p>
            <p className="mt-1 font-body text-sm text-gray-400">{t('customers.notFoundDetail')}</p>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <div className="flex items-start justify-between gap-2">
                <h1 className="font-display text-xl font-semibold text-gray-900">{customerName}</h1>
                <button
                  type="button"
                  onClick={handleOpenReminderFromHeader}
                  className="leading-none inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-amber-400 bg-transparent px-3 py-1.5 font-body text-sm font-semibold text-amber-900 hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                >
                  <Icon size="sm"><CalendarIcon /></Icon>
                  {t('reminder.setReminder')}
                </button>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <p className="font-body text-sm text-gray-500">{t('customers.activityLog')}</p>
                <span className="rounded-full bg-green-100 px-2 py-0.5 font-body text-xs font-semibold text-green-800">
                  {t('customers.notesCount', { count: allLogs.length })}
                </span>
              </div>
            </div>

            <AddNoteForm onSave={handleSaveNote} />
            <LogList
              logs={allLogs}
              currentUserId={user?.uid}
              canDeleteAny={hasPermission('customers.delete')}
              onEdit={handleEditLog}
              onDelete={handleDeleteLog}
              onRemind={handleRemindFromLog}
            />

            <ReminderModal
              isOpen={reminderOpen}
              onClose={() => setReminderOpen(false)}
              crops={reminderCrops}
              initialDescription={reminderDescription}
              customerInfo={customerInfo}
            />
          </>
        )}
      </main>
    </div>
  );
}
